import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GetRowIdParams, GridOptions, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';

import { MacSerialNumberDevice } from '@shure/cloud/shared/models/devices';
import { BaseTableComponent } from '@shure/cloud/shared/ui/components';
import { AgGridResponsiveWrapperComponent } from '@shure/shared/angular/ui/components/ag-grid-next';

import { SelectedLocalNetworkDeviceIdMap } from '../../../../../models/step2-discover-devices.models';

interface MacSerialTableContext {
	componentParent: MacSerialTableComponent;
}

@Component({
	selector: 'sh-mac-serial-table',
	standalone: true,
	imports: [AgGridAngular, AgGridResponsiveWrapperComponent],
	templateUrl: './mac-serial-table.component.html',
	styleUrls: ['./mac-serial-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MacSerialTableComponent extends BaseTableComponent<MacSerialNumberDevice> {
	@Output() public selectionChange = new EventEmitter<SelectedLocalNetworkDeviceIdMap>();

	@Input()
	public set devices(_devices: MacSerialNumberDevice[] | null) {
		this.gridData = _devices ?? [];
	}

	// AG Grid Members
	public gridData: MacSerialNumberDevice[] = [];

	public context: MacSerialTableContext = {
		componentParent: this
	};

	public readonly columnDefs: ColDef<MacSerialNumberDevice>[] = [
		//
		// checkbox selector column
		//
		this.createCheckboxColumn({
			cellClass: (params) => [
				'mac-serial-table-checkbox-cell',
				`mac-serial-table-checkbox-cell-${params.data?.device.id}`
			]
		}),
		//
		// MAC Address
		//
		this.createMacAddressColumn({
			field: 'device.macAddress',
			lockVisible: true
		}),
		//
		// Serial Number
		//
		this.createSerialNumberColumn({
			field: 'device.serialNumber',
			lockVisible: true
		})
	];

	constructor(translocoService: TranslocoService) {
		super(translocoService);
		this.context.componentParent = this;
	}

	public override onGridReady(params: GridReadyEvent<MacSerialNumberDevice>): void {
		super.onGridReady(params);
		// the UX team wants all mac/serial rows selected by default.
		this.gridApi?.selectAll();
	}

	public getRowId(params: GetRowIdParams<MacSerialNumberDevice>): string {
		return params.data.device.id;
	}

	public onSelectionChanged(params: SelectionChangedEvent<MacSerialNumberDevice>): void {
		this.emitSelectionChange(params.api.getSelectedRows());
	}

	protected override getGridOptions(): GridOptions<MacSerialNumberDevice> {
		return {
			...super.getGridOptions(),
			// Attaching the AG Grid popups to the document body currently does not work with Prism dialogs.
			// For now we'll attach to the grid and take the hit on menus getting cut off on smaller tables.
			// TODO: remove this once Prism dialogs have better AG Grid support
			popupParent: undefined
		};
	}

	private emitSelectionChange(selectedRows: MacSerialNumberDevice[]): void {
		this.selectionChange.emit({
			allSelected: selectedRows.length > 0 && selectedRows.length === this.gridData.length,
			devices: selectedRows.reduce(
				(obj, row) => ({
					...obj,
					[row.device.id]: row
				}),
				{}
			)
		});
	}
}
